/**
 * Shared constants and helpers for email block components.
 * These are used by both the Canvas (React render) and the HTML Generator (renderToStaticMarkup).
 */

import { createContext, useContext } from 'react';
import type { SmartButtonType } from '../../../email-builder/emailBuilder.store';

// ─── Device mode context ──────────────────────────────────────────────────────
// Consumed by two-column blocks to stack columns on mobile canvas preview.
// The HTML generator always renders desktop layout (media queries handle email clients).

export const DeviceModeContext = createContext<'desktop' | 'mobile'>('desktop');

export const useDeviceMode = () => useContext(DeviceModeContext);

export const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";

export const EMAIL_COLORS = {
  bodyBg: "#f8f7f4",
  containerBg: "#ffffff",
  textPrimary: "#3d3a33",
  textDark: "#1c1a15",
  textMuted: "#75706a",
  border: "rgb(28 24 18 / 0.09)",
  smartBadgeBg: "rgb(15 92 69 / 0.1)",
  smartBadgeText: "#0a4530",
  primaryBtn: "#0f5c45",
  primaryBtnText: "#ffffff",
  headerBg: "#1c1a15",
  footerBg: "#f2f0ea",
  footerBorder: "rgb(28 24 18 / 0.15)",
  footerText: "#75706a",
  placeholder: "#f2f0ea",
  starFilled: "#b5803d",
  starEmpty: "#d9d5cc",
} as const;

/** Escape attribute values (not inner HTML) */
export const attr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Injects `color` and `line-height` as inline styles on every block-level
 * text element produced by the rich-text editor.  Email clients (Gmail,
 * Outlook, Apple Mail dark-mode) do NOT reliably inherit CSS from a parent
 * wrapper div, so we must stamp the values directly onto each tag.
 *
 * Tags handled: p, li, h1–h6, blockquote, td (when used as text container).
 * Existing inline style attributes are preserved and extended.
 */
export const injectTextStyles = (
  html: string,
  color: string,
  lineHeight: number,
): string => {
  if (!html) return html;

  const BLOCK_TAGS = ['p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote'];
  const tagPattern = BLOCK_TAGS.join('|');

  // Match opening tags for block-level text elements (with or without existing attributes)
  return html.replace(
    new RegExp(`<(${tagPattern})(\\s[^>]*)?>`, 'gi'),
    (_match, tag: string, attrs: string = '') => {
      const newProps = `color:${color};line-height:${lineHeight};`;

      // If a style attribute already exists, prepend our values
      if (/style\s*=/i.test(attrs)) {
        return `<${tag}${attrs.replace(
          /style\s*=\s*["']([^"']*)["']/i,
          (_m: string, existing: string) => `style="${newProps}${existing}"`,
        )}>`;
      }

      // No existing style — add one
      return `<${tag}${attrs} style="${newProps}">`;
    },
  );
};

// ─── Smart button metadata ────────────────────────────────────────────────────

export interface SmartButtonMeta {
  label: string;
  icon: string;
  group: string;
}

export const SMART_BUTTON_META: Record<SmartButtonType, SmartButtonMeta> = {
  smartBtn_viewTreatmentPlan:         { label: 'View My Treatment Plan',        icon: 'file-text',      group: 'Financial & Treatment' },
  smartBtn_checkInsuranceEligibility: { label: 'Check Insurance Eligibility',   icon: 'shield',         group: 'Financial & Treatment' },
  smartBtn_payOnlineBalance:          { label: 'Pay Online Balance',             icon: 'credit-card',    group: 'Financial & Treatment' },
  smartBtn_bookAppointment:           { label: 'Book Your Appointment',          icon: 'calendar',       group: 'Scheduling & Triage' },
  smartBtn_scheduleCleaning:          { label: 'Schedule My Cleaning',           icon: 'calendar-check', group: 'Scheduling & Triage' },
  smartBtn_emergencyCall:             { label: 'Got Emergency? Call Now',        icon: 'phone',          group: 'Scheduling & Triage' },
  smartBtn_claimOffer:                { label: 'Claim My $99 Offer',             icon: 'tag',            group: 'Offers & Claims' },
  smartBtn_activateInvisalign:        { label: 'Activate My Invisalign Offer',   icon: 'sparkles',       group: 'Offers & Claims' },
  smartBtn_completeIntakeForm:        { label: 'Complete My Intake Form',        icon: 'file',           group: 'Administrative & Loyalty' },
  smartBtn_leaveReview:               { label: 'Leave 5 Star Review',            icon: 'star',           group: 'Administrative & Loyalty' },
  smartBtn_referFriend:               { label: 'Refer a Friend, Earn $50',       icon: 'gift',           group: 'Administrative & Loyalty' },
};

/** Inline SVG icons for email-safe rendering (no icon font dependency) */
export const BUTTON_ICON_SVG: Record<string, string> = {
  'file-text': '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
  'shield':     '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
  'credit-card':'<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>',
  'calendar':   '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
  'calendar-check':'<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg>',
  'phone':      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>',
  'tag':        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>',
  'sparkles':   '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>',
  'file':       '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>',
  'star':       '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
  'gift':       '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>',
};


